import Draggabilly from 'draggabilly';
import { destroyExternalVideo } from '../../2-molecules/external-video/external-video';

class ShortsSlider {
  $shortsSlider: HTMLElement;
  $shorts: HTMLElement;
  $scrollBar: HTMLElement;
  $thumb: HTMLElement;
  $$short: NodeListOf<HTMLElement>;
  $leftScrollButton: HTMLElement | null = null;
  $rightScrollButton: HTMLElement | null = null;
  thumbLeft = 0;
  enableScrollbarUpdates = true;
  enableLeftScrollButton = false;
  enableRightScrollButton = false;

  constructor($shortsSlider: HTMLElement) {
    this.$shortsSlider = $shortsSlider;

    const $shorts = this.$shortsSlider.querySelector<HTMLElement>(
      '.shorts-slider__shorts',
    );

    const $$short = this.$shortsSlider.querySelectorAll<HTMLElement>(
      '.shorts-slider__short',
    );

    const $scrollBar = this.$shortsSlider.querySelector<HTMLElement>(
      '.shorts-slider__scroll-bar',
    );

    const $thumb = $scrollBar?.querySelector<HTMLElement>(
      '.shorts-slider__scroll-bar-thumb',
    );

    if (!$shorts || !$scrollBar || !$thumb) {
      throw new Error('Shorts and/or scroll bar are missing');
    }

    this.$shorts = $shorts;
    this.$scrollBar = $scrollBar;
    this.$thumb = $thumb;
    this.$$short = $$short;

    // Init everything
    this.initScrollButtons();
    this.initScrollBar();
    this.initSwitcher();

    // Force to scroll position to zero on load
    window.requestAnimationFrame(() => {
      this.$shorts.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
    });
  }

  initSwitcher() {
    this.$$short.forEach(($short) => {
      $short.addEventListener('click', (event) => {
        const { target } = event;

        if (
          target instanceof HTMLElement &&
          target.closest('.external-video__overlay-button')
        ) {
          this.$$short.forEach(($otherShort) => {
            if ($otherShort !== $short) {
              const $externalVideo =
                $otherShort.querySelector<HTMLElement>('.external-video');

              if ($externalVideo) {
                destroyExternalVideo($externalVideo);
              }
            }
          });
        }
      });
    });
  }

  initScrollBar() {
    // Update on scroll and initial load
    this.$shorts.addEventListener('scroll', () => this.update(), {
      passive: true,
    });

    // Update on resizing of scroll bar
    const resizeObserver = new ResizeObserver(() => this.update());
    resizeObserver.observe(this.$scrollBar);

    // Move on backdrop click
    this.$scrollBar
      .querySelector('.shorts-slider__scroll-bar-backdrop')
      ?.addEventListener('click', (event) => {
        // @ts-expect-error layerX is a non-standard, but well supported attribute on MouseEvent
        const { layerX } = event;

        this.$shorts.scrollLeft =
          (layerX / this.$scrollBar.offsetWidth) * this.$shorts.scrollWidth;
      });

    const draggie = new Draggabilly(this.$thumb, {
      axis: 'x',
      containment: true,
    });

    draggie.on('dragStart', () => {
      this.enableScrollbarUpdates = false;
      this.$shorts.classList.add('shorts-slider__shorts--was-dragged');
    });

    draggie.on('dragMove', (event, pointer, moveVector) => {
      this.$shorts.scrollLeft =
        ((this.thumbLeft + moveVector.x) / this.$scrollBar.offsetWidth) *
        this.$shorts.scrollWidth;
    });

    draggie.on('dragEnd', () => {
      this.enableScrollbarUpdates = true;
      this.$shorts.classList.remove('shorts-slider__shorts--was-dragged');
      this.update();
    });
  }

  initScrollButtons() {
    // Left scroll button
    this.$leftScrollButton = this.$shortsSlider.querySelector(
      '.shorts-slider__navigation-button--left',
    );

    this.$leftScrollButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.move('left');
    });

    // Right scroll button
    this.$rightScrollButton = this.$shortsSlider.querySelector(
      '.shorts-slider__navigation-button--right',
    );

    this.$rightScrollButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.move('right');
    });
  }

  move(direciton: 'left' | 'right') {
    const cardWidth = this.$shorts.querySelector<HTMLElement>(
      '.shorts-slider__short',
    )?.offsetWidth;

    if (cardWidth) {
      this.$shorts.scrollTo({
        left:
          this.$shorts.scrollLeft +
          (direciton === 'left' ? cardWidth * -1 : cardWidth),
      });
    }
  }

  update() {
    if (!this.enableScrollbarUpdates) {
      return;
    }

    window.requestAnimationFrame(() => {
      const width = (this.$shorts.offsetWidth / this.$shorts.scrollWidth) * 100;
      this.thumbLeft =
        (this.$shorts.scrollLeft / this.$shorts.scrollWidth) *
        this.$scrollBar.offsetWidth;

      this.$thumb.style.setProperty('width', `${width}%`);
      this.$thumb.style.setProperty('left', `${this.thumbLeft}px`);

      this.$scrollBar.classList.toggle(
        'shorts-slider__scroll-bar--enabled',
        width < 100,
      );

      this.$leftScrollButton?.toggleAttribute(
        'disabled',
        this.$shorts.scrollLeft === 0,
      );

      this.$rightScrollButton?.toggleAttribute(
        'disabled',
        this.$shorts.scrollWidth <=
          this.$shorts.scrollLeft + this.$shorts.offsetWidth,
      );
    });
  }
}

document
  .querySelectorAll<HTMLElement>('.shorts-slider')
  .forEach(($shortsSlider) => new ShortsSlider($shortsSlider));

export default ShortsSlider;
